/*
 * Tracking Event Report Search Service - Returns tracking events that have reports that match
 * a full text search.
 * 
 * Angular module: trackerbase-app.services.tracking-event-report-search-service
 * Exposed Service: EventReportSearch
 *
 */

import angular from "angular";

import Assert from "tbAssert";

import SearchParsingError from "../models/errors/SearchParsingError";

import TbAppConfigDataServiceModule from "../config/tb-app-config/TbAppConfigDataService";
import TrackingEventFactoriesModule from "../models/factories/tracking-event-factories";
import ServerFilterProcessorModule from "./ServerFilterProcessorService";
import ServerSortProcessorModule from "./ServerSortProcessorService";
import SearchRequestParserServiceModule from "./search-request-parser";

const mLogSvc = new WeakMap();
const mHttpSvc = new WeakMap();
const mQSvc = new WeakMap();
const mTrackingEventFactorySvc = new WeakMap();
const mServerFilterProcessorSvc = new WeakMap();
const mServerSortProcessorSvc = new WeakMap();
const mSearchRequestParserSvc = new WeakMap();
const mEventSearchApiUrl = new WeakMap();

class EventReportSearchService {
    constructor(
        $log,
        $http,
        $q,
        TbAppConfigDataService,
        TrackingEventFactory,
        ServerFilterProcessor,
        ServerSortProcessor,
        SearchRequestParserService)
    {

        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");

        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");
        Assert.StrictAssert(TrackingEventFactory != null, "Assertion Failure: Missing Tracking Event Factory Service Dependency");
        Assert.StrictAssert(ServerFilterProcessor != null, "Assertion Failure: Missing Server Filter Processor Service Dependency");
        Assert.StrictAssert(ServerSortProcessor != null, "Assertion Failure: Missing Server Sort Processor Service Dependency");
        Assert.StrictAssert(SearchRequestParserService != null, "Assertion Failure: Missing Search Request Parser Service Dependency");

        mLogSvc.set(this, $log);
        mHttpSvc.set(this, $http);
        mQSvc.set(this, $q);
        mTrackingEventFactorySvc.set(this, TrackingEventFactory);
        mServerFilterProcessorSvc.set(this, ServerFilterProcessor);
        mServerSortProcessorSvc.set(this, ServerSortProcessor);
        mSearchRequestParserSvc.set(this, SearchRequestParserService);
        mEventSearchApiUrl.set(this, `${TbAppConfigDataService.tbServerBaseUrl}/api/trackingevents/search`);

        mLogSvc.get(this).debug("Event Report Search Service Initialized");
    }

    toString() {
        return "Event Report Search Service";
    }

    SearchReports(searchString, pageNum, pageSize, filterObj, sortObj) {
        let searchRequestBody = null;

        if ((!angular.isString(searchString)) || (searchString === "")) {
            // Fake a no search results return when we are passed a non-string (inc. undefined and null) or the empty string.
            return mQSvc.get(this).when({
                eventData: [],
                totalItems: 0
            });
        }

        try {
            searchRequestBody = mSearchRequestParserSvc.get(this).buildSearchRequest(searchString);
        }
        catch (buildEx) {
            if (buildEx instanceof(SearchParsingError)) {
                if (angular.isDefined(buildEx.message)) {
                    return mQSvc.get(this).reject(buildEx.message);
                }
                else {
                    return mQSvc.get(this).reject("An error occurred while parsing your request. Please check the log for further details.");
                }
            }

            throw buildEx;
        }

        if (searchRequestBody == null) {
            return mQSvc.get(this).reject("Invalid search string. Please check your search and try again.");
        }

        let pageNumberParam = 1;
        if ((Number.isInteger(pageNum)) && (pageNum > 0)) {
            pageNumberParam = pageNum;
        }

        let pageSizeParam = 25;
        if ((Number.isInteger(pageSize)) && (pageSize > 0)) {
            pageSizeParam = pageSize;
        }

        let searchParams = {
            pageNumber: pageNumberParam,
            pageSize: pageSizeParam
        };

        if (filterObj != null) {
            let filterParam = mServerFilterProcessorSvc.get(this).ProcessFilterForServer(filterObj,"||");
            searchParams["q"] = filterParam;
        }

        if (sortObj != null) {
            let sortParam = mServerSortProcessorSvc.get(this).ProcessEventSort(sortObj);
            if (sortParam != null) {
                searchParams["sort"] = sortParam;
            }
        }

        let searchUrl = mEventSearchApiUrl.get(this);
        let searchPromise = mHttpSvc.get(this)({
            method: "POST",
            url: searchUrl,
            params: searchParams,
            data: searchRequestBody
        })
        .then(
            (response) => {
                let responseData = response.data;
                if (responseData.Items != null) {
                    let responseEvents = responseData.Items;
                    let processedEvents = responseEvents.map(
                        (serverEvent) => mTrackingEventFactorySvc.get(this).ParseServerEvent(serverEvent)
                    );

                    return {
                        totalItems: responseData.TotalItems,
                        eventData: processedEvents
                    };
                }
                else {
                    return mQSvc.get(this).reject("Missing Response Data");
                }
            },
            (errorResponse) => {
                let errorMessage = "";
                try {
                    errorMessage = errorResponse.data.ModelState.searchRequest[0];
                }
                catch (ex) {
                    mLogSvc.get(this).error("Search Request: Unexpected Error");
                    mLogSvc.get(this).error(errorResponse);
                    errorMessage = "An error occurred processing your search request. Try your request later, or contact TrackerBase support if the error continues to occur.";
                }

                return mQSvc.get(this).reject(errorMessage);
            }
        );

        return searchPromise;
    }
}

EventReportSearchService.$inject = [
    "$log",
    "$http",
    "$q",
    "TbAppConfigDataService",
    "TrackingEventFactory",
    "ServerFilterProcessor",
    "ServerSortProcessor",
    "SearchRequestParserService"
];

export default angular
    .module("trackerbase-app.services.event-report-search-service", [
        TbAppConfigDataServiceModule,
        TrackingEventFactoriesModule,
        ServerFilterProcessorModule,
        ServerSortProcessorModule,
        SearchRequestParserServiceModule
    ])
    .service("EventReportSearchService", EventReportSearchService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/TrackingEventReportSearchService.js
// module id = CItK
// module chunks = 1